import { regionEn } from "@/lib/seo-keywords";
// EN twin of triple-key combo: /en/translate/$language/$doctype/in/$location
// Phase P-EN-TRIPLE-KEY — mirrors TH triple-key with hreflang pairing.
// 18 languages × 15 doctypes × 14 hub locations = 3,780 EN long-tail pages.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguage,
} from "@/data/translation-languages";
import {
  TRANSLATION_DOCTYPES,
  getTranslationDocType,
} from "@/data/translation-doctypes";
import { getLocation } from "@/data/locations";
import { TRIPLE_KEY_LOCATIONS } from "@/routes/translate.$language.$doctype.in.$location";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import TripleKeyCrossLinks from "@/components/TripleKeyCrossLinks";
import {
  Languages, MapPin, FileCheck2, ShieldCheck, BadgeCheck, Clock,
  Phone, MessageCircle, ArrowRight, Globe2, Stamp,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const LOC_SET = new Set<string>(TRIPLE_KEY_LOCATIONS);

export const Route = createFileRoute("/en/translate/$language/$doctype/in/$location")({
  loader: ({ params }) => {
    const lang = getTranslationLanguage(params.language);
    const doc = getTranslationDocType(params.doctype);
    const loc = getLocation(params.location);
    if (!lang || !doc || !loc) throw notFound();
    if (!LOC_SET.has(params.location)) throw notFound();
    return {
      langSlug: params.language,
      docSlug: params.doctype,
      locSlug: params.location,
    };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    const d = getTranslationDocType(params.doctype);
    const loc = getLocation(params.location);
    if (!l || !d || !loc || !LOC_SET.has(params.location)) {
      return { meta: [{ title: ctaTitle("Page not found", "/en/translate/$language/$doctype/in/$location", "en")}] };
    }
    const thPath = `/translate/${l.slug}/${d.slug}/in/${loc.slug}`;
    const enPath = `/en/translate/${l.slug}/${d.slug}/in/${loc.slug}`;
    const url = `${SITE}${enPath}`;
    const price = (l.priceFromTh + d.surcharge).toLocaleString();
    const title = `${d.nameEn} ${l.nameEn} Translation in ${loc.nameEn} — from THB ${price}/page | NYC`;
    const desc = `Certified ${l.nameEn} translation of ${d.nameEn} for clients in ${loc.nameEn}, Thailand — sworn MoJ${l.isNaati ? " + NAATI": ""} translators, accepted by embassies, consulates, MFA & courts. ${l.turnaround}. Door-to-door pickup in ${loc.nameEn}. Talk to a specialist today.`;

    const faqs = [
      {
        q: `How much does ${d.nameEn} translation into ${l.nameEn} cost in ${loc.nameEn}?`,
        a: `From THB ${price} per A4 page — certified by sworn translators with NYC stamp, accepted by embassies, consulates, MFA and Thai courts. Standard turnaround ${l.turnaround}. Door-to-door pickup available in ${loc.nameEn}.`,
      },
      {
        q: `What certifications do I need for ${d.nameEn}?`,
        a: `Most use-cases require: ${d.certifications.join(" → ")}. We handle the full chain in one engagement — no need to run between offices.`,
      },
      ...d.faqExtra.slice(0, 2),
    ];

    const ratingKey = `en-translate/${l.slug}/${d.slug}/${loc.slug}`;
    const rating = aggregateRatingNode(ratingKey);

    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/$language/$doctype/in/$location", "en") },
        { name: "description", content: ctaDesc(desc, "/en/translate/$language/$doctype/in/$location", "en")},
        { name: "keywords", content: [
          `${l.nameEn} translation ${loc.nameEn}`,
          `${d.nameEn} translation ${loc.nameEn}`,
          `certified ${l.nameEn} translation ${d.nameEn}`,
          `sworn ${l.nameEn} translator ${loc.nameEn}`,
          `MFA legalization ${loc.nameEn}`,
          ...l.keywords.slice(0, 3),
        ].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/translate/$language/$doctype/in/$location", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/translate/$language/$doctype/in/$location", "en")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(title, "/en/translate/$language/$doctype/in/$location", "en")},
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(thPath, enPath),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${d.nameEn} → ${l.nameEn} Certified Translation · ${loc.nameEn}`,
              serviceType: "Certified document translation",
              category: d.category,
              inLanguage: "en",
              areaServed: {
                "@type": "Place",
                name: loc.nameEn,
                ...(loc.lat && loc.lng
                  ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
                  : {}),
              },
              availableLanguage: [
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
                { "@type": "Language", name: "Thai", iso6391: "th" },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: l.priceFromTh + d.surcharge,
                priceCurrency: "THB",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: l.priceFromTh + d.surcharge,
                  priceCurrency: "THB",
                  unitText: "page",
                },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LegalService",
              "@id": `${url}#localbusiness`,
              name: `NYC Legal & Notary Service — ${d.nameEn} ${l.nameEn} (${loc.nameEn})`,
              url,
              telephone: "+66832494999",
              priceRange: "฿฿",
              address: {
                "@type": "PostalAddress",
                streetAddress: loc.address ?? "61 Soi Lat Phrao 95",
                addressLocality: loc.nameEn,
                postalCode: loc.postalCode ?? "10310",
                addressCountry: "TH",
              },
              areaServed: { "@type": "Place", name: loc.nameEn },
              openingHours: "Mo-Sa 09:00-18:00",
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `How to translate ${d.nameEn} into ${l.nameEn} for ${loc.nameEn} clients`,
              inLanguage: "en",
              step: [
                { "@type": "HowToStep", position: 1, name: "Send scan via LINE @nycli", text: `Photograph the ${d.nameEn} and message LINE @nycli — quote within 15 minutes.` },
                { "@type": "HowToStep", position: 2, name: "Confirm & deposit", text: `Wire 50% deposit to start the ${l.nameEn} translation.` },
                { "@type": "HowToStep", position: 3, name: "Translate & certify", text: `Sworn translator translates and stamps the document, then ${d.certifications.join(" → ")}.` },
                { "@type": "HowToStep", position: 4, name: "Delivery", text: `Pickup at HQ or EMS/Messenger door-to-door to ${loc.nameEn}.` },
              ],
              totalTime: `PT${l.turnaround.includes("ชั่วโมง") || l.turnaround.toLowerCase().includes("hour") ? "4H" : "48H"}`,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Translation", item: `${SITE}/en/translate` },
                { "@type": "ListItem", position: 3, name: `${l.nameEn}`, item: `${SITE}/en/translate/${l.slug}` },
                { "@type": "ListItem", position: 4, name: `${d.nameEn}`, item: `${SITE}/en/translate/${l.slug}/${d.slug}` },
                { "@type": "ListItem", position: 5, name: loc.nameEn, item: url },
              ],
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 2).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: EnTripleKeyPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <Link to="/en/translate" className="mt-4 inline-block text-gold underline">
        Back to Translation hub
      </Link>
    </main>
  ),
});

function EnTripleKeyPage() {
  const { langSlug, docSlug, locSlug } = Route.useLoaderData() as {
    langSlug: string; docSlug: string; locSlug: string;
  };
  const l = getTranslationLanguage(langSlug)!;
  const d = getTranslationDocType(docSlug)!;
  const loc = getLocation(locSlug)!;
  const otherLangs = TRANSLATION_LANGUAGES.filter((x) => x.slug !== langSlug).slice(0, 8);
  const otherDocs = TRANSLATION_DOCTYPES.filter((x) => x.slug !== docSlug).slice(0, 8);
  const otherLocs = TRIPLE_KEY_LOCATIONS.filter((x) => x !== locSlug).slice(0, 8);
  const price = l.priceFromTh + d.surcharge;

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate" className="hover:text-gold">Translation</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/$language" params={{ language: l.slug }} className="hover:text-gold">{l.nameEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/$language/$doctype" params={{ language: l.slug, doctype: d.slug }} className="hover:text-gold">{d.nameEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{loc.nameEn}</span>
          </nav>

          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> Certified · {d.nameEn} → {l.nameEn} · {loc.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {d.nameEn} → {l.nameEn} Certified Translation in {loc.nameEn}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Certified {l.nameEn} translation of {d.nameEn} for clients in {loc.nameEn} —
            from THB {price.toLocaleString()}/page, completed in {l.turnaround}. Door-to-door pickup
            and delivery across {regionEn(loc.region)}.
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "From", v: `THB ${price.toLocaleString()}/page` },
              { k: "Turnaround", v: l.turnaround },
              { k: "Typical volume", v: d.pagesEstimate },
              { k: "Service area", v: loc.nameEn },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> Send {d.nameEn} via LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) provides certified {l.nameEn} translation of {d.nameEn}
          for clients in {loc.nameEn}, Thailand — sworn translators, NYC stamp, from THB {price.toLocaleString()}/page,
          completed in {l.turnaround}. Accepted by embassies, consulates, MFA and Thai courts. Use case: {d.intent}.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <MapPin className="inline h-6 w-6 text-amber-600" /> Scope — {d.nameEn} → {l.nameEn}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            {d.description} For clients in {loc.nameEn} we provide the full chain — pickup, certified translation by sworn translator, legalization, delivery — without you leaving the office.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Stamp className="inline h-6 w-6 text-emerald-600" /> Certification chain ({d.certifications.length} steps)
          </h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2">
            {d.certifications.map((c, i) => (
              <li key={c} className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-600 text-xs font-bold text-white">{i + 1}</span>
                <div>
                  <div className="font-display text-sm font-extrabold text-navy-deep">{c}</div>
                  <div className="mt-0.5 text-[12px] text-slate-600">NYC handles every step — you don't run anywhere</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Typical destinations</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {d.destinationUses.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Countries using {l.nameEn}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {l.countryOfUse.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Globe2 className="h-3.5 w-3.5 text-amber-600" /> {c}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Price · {loc.nameEn}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {d.nameEn} → {l.nameEn} from THB {price.toLocaleString()}/page
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Sworn translator stamp + signature</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Completed in {l.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> EMS pickup/delivery across {regionEn(loc.region)}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> MFA + embassy legalization bundled</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently Asked</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={[
              {
                q: `How much does ${d.nameEn} → ${l.nameEn} cost in ${loc.nameEn}?`,
                a: `From THB ${price.toLocaleString()}/page — certified by sworn translator, completed in ${l.turnaround}, accepted by embassies, consulates, MFA and Thai courts.`,
              },
              {
                q: `What certifications does ${d.nameEn} need?`,
                a: `${d.certifications.join(" → ")} — bundled in a single engagement.`,
              },
              ...d.faqExtra.slice(0, 2),
            ]} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other documents → {l.nameEn} ({loc.nameEn})</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherDocs.map((od) => (
              <Link
                key={od.slug}
                to="/en/translate/$language/$doctype/in/$location"
                params={{ language: l.slug, doctype: od.slug, location: loc.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{od.nameEn}</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{od.nameEn}</div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{d.nameEn} → other languages ({loc.nameEn})</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherLangs.map((ol) => (
              <Link
                key={ol.slug}
                to="/en/translate/$language/$doctype/in/$location"
                params={{ language: ol.slug, doctype: d.slug, location: loc.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{ol.nameEn}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  {d.nameEn} → {ol.nameEn}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">from THB {(ol.priceFromTh + d.surcharge).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{d.nameEn} → {l.nameEn} in other hubs</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherLocs.map((ls) => {
              const other = getLocation(ls);
              if (!other) return null;
              return (
                <Link
                  key={ls}
                  to="/en/translate/$language/$doctype/in/$location"
                  params={{ language: l.slug, doctype: d.slug, location: ls }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{other.region}</div>
                  <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {other.nameEn}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>

      <TripleKeyCrossLinks
        mode="en"
        language={l.slug}
        doctype={d.slug}
        languageNameTh={l.nameTh}
        languageNameEn={l.nameEn}
        doctypeNameTh={d.nameTh}
        doctypeNameEn={d.nameEn}
      />

      <TrustLayer variant="compact" />
    </main>
  );
}
